// @ts-nocheck
import { useMutation, useQuery } from "@tanstack/vue-query";
import type {
  DataTag,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationReturnType,
  UseQueryOptions,
  UseQueryReturnType,
} from "@tanstack/vue-query";

import { computed, unref } from "vue";
import type { MaybeRef } from "vue";

import type {
  BalanceReadModel,
  ChildCardNumberData,
  ChildReadModel,
  GetExternalBulkOrdersHistory200Item,
  GetExternalBulkOrdersHistoryParams,
  SaveUserLocaleInputModel,
  UserReadModel,
} from "../models";

import { customInstance } from "../../../src/custom-axios";
import type { ErrorType, BodyType } from "../../../src/custom-axios";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

export type getCurrentUserResponse200 = {
  data: UserReadModel;
  status: 200;
};

export type getCurrentUserResponseSuccess = getCurrentUserResponse200 & {
  headers: Headers;
};
export type getCurrentUserResponse = getCurrentUserResponseSuccess;

export const getGetCurrentUserUrl = () => {
  return `/api/frontend/auth/currentUser`;
};

export const getCurrentUser = async (options?: RequestInit): Promise<getCurrentUserResponse> => {
  return customInstance<getCurrentUserResponse>(getGetCurrentUserUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetCurrentUserQueryKey = () => {
  return ["api", "frontend", "auth", "currentUser"] as const;
};

export const getGetCurrentUserQueryOptions = <
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>>;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = getGetCurrentUserQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCurrentUser>>> = ({ signal }) =>
    getCurrentUser({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCurrentUser>>,
    TError,
    TData
  >;
};

export type GetCurrentUserQueryResult = NonNullable<Awaited<ReturnType<typeof getCurrentUser>>>;
export type GetCurrentUserQueryError = ErrorType<unknown>;

export function useGetCurrentUser<
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryReturnType<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCurrentUserQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryReturnType<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = unref(queryOptions).queryKey as DataTag<QueryKey, TData, TError>;

  return query;
}

export type getChildrenGetOneResponse200 = {
  data: ChildReadModel;
  status: 200;
};

export type getChildrenGetOneResponseSuccess = getChildrenGetOneResponse200 & {
  headers: Headers;
};
export type getChildrenGetOneResponse = getChildrenGetOneResponseSuccess;

export const getGetChildrenGetOneUrl = (childId: string) => {
  return `/api/frontend/children/${childId}`;
};

export const getChildrenGetOne = async (
  childId: string,
  options?: RequestInit,
): Promise<getChildrenGetOneResponse> => {
  return customInstance<getChildrenGetOneResponse>(getGetChildrenGetOneUrl(childId), {
    ...options,
    method: "GET",
  });
};

export const getGetChildrenGetOneQueryKey = (childId?: MaybeRef<string>) => {
  return ["api", "frontend", "children", childId] as const;
};

export const getGetChildrenGetOneQueryOptions = <
  TData = Awaited<ReturnType<typeof getChildrenGetOne>>,
  TError = ErrorType<unknown>,
>(
  childId: MaybeRef<string>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChildrenGetOne>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = getGetChildrenGetOneQueryKey(childId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getChildrenGetOne>>> = ({ signal }) =>
    getChildrenGetOne(unref(childId), { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: computed(() => !!unref(childId)),
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getChildrenGetOne>>, TError, TData>;
};

export type GetChildrenGetOneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getChildrenGetOne>>
>;
export type GetChildrenGetOneQueryError = ErrorType<unknown>;

export function useGetChildrenGetOne<
  TData = Awaited<ReturnType<typeof getChildrenGetOne>>,
  TError = ErrorType<unknown>,
>(
  childId: MaybeRef<string>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChildrenGetOne>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryReturnType<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetChildrenGetOneQueryOptions(childId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryReturnType<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = unref(queryOptions).queryKey as DataTag<QueryKey, TData, TError>;

  return query;
}

export type putChildrenChangeCardNumberResponseDefault = {
  data: void;
  status: number;
};
export type putChildrenChangeCardNumberResponseError =
  putChildrenChangeCardNumberResponseDefault & {
    headers: Headers;
  };

export type putChildrenChangeCardNumberResponse = putChildrenChangeCardNumberResponseError;

export const getPutChildrenChangeCardNumberUrl = (childId: string) => {
  return `/api/frontend/children/${childId}/changeCardNumber`;
};

export const putChildrenChangeCardNumber = async (
  childId: string,
  childCardNumberData: ChildCardNumberData,
  options?: RequestInit,
): Promise<putChildrenChangeCardNumberResponse> => {
  return customInstance<putChildrenChangeCardNumberResponse>(
    getPutChildrenChangeCardNumberUrl(childId),
    {
      ...options,
      method: "PUT",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(childCardNumberData),
    },
  );
};

export const getPutChildrenChangeCardNumberMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putChildrenChangeCardNumber>>,
    TError,
    { childId: string; data: BodyType<ChildCardNumberData> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putChildrenChangeCardNumber>>,
  TError,
  { childId: string; data: BodyType<ChildCardNumberData> },
  TContext
> => {
  const mutationKey = ["putChildrenChangeCardNumber"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putChildrenChangeCardNumber>>,
    { childId: string; data: BodyType<ChildCardNumberData> }
  > = (props) => {
    const { childId, data } = props ?? {};

    return putChildrenChangeCardNumber(childId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutChildrenChangeCardNumberMutationResult = NonNullable<
  Awaited<ReturnType<typeof putChildrenChangeCardNumber>>
>;
export type PutChildrenChangeCardNumberMutationBody = BodyType<ChildCardNumberData>;
export type PutChildrenChangeCardNumberMutationError = ErrorType<void>;

export const usePutChildrenChangeCardNumber = <TError = ErrorType<void>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof putChildrenChangeCardNumber>>,
      TError,
      { childId: string; data: BodyType<ChildCardNumberData> },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationReturnType<
  Awaited<ReturnType<typeof putChildrenChangeCardNumber>>,
  TError,
  { childId: string; data: BodyType<ChildCardNumberData> },
  TContext
> => {
  const mutationOptions = getPutChildrenChangeCardNumberMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

export type getExternalBulkOrdersHistoryResponse200 = {
  data: GetExternalBulkOrdersHistory200Item[];
  status: 200;
};

export type getExternalBulkOrdersHistoryResponseSuccess =
  getExternalBulkOrdersHistoryResponse200 & {
    headers: Headers;
  };
export type getExternalBulkOrdersHistoryResponse = getExternalBulkOrdersHistoryResponseSuccess;

export const getGetExternalBulkOrdersHistoryUrl = (
  institutionId: string,
  params: GetExternalBulkOrdersHistoryParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/frontend/externalBulkOrders/institutions/${institutionId}/history?${stringifiedParams}`
    : `/api/frontend/externalBulkOrders/institutions/${institutionId}/history`;
};

export const getExternalBulkOrdersHistory = async (
  institutionId: string,
  params: GetExternalBulkOrdersHistoryParams,
  options?: RequestInit,
): Promise<getExternalBulkOrdersHistoryResponse> => {
  return customInstance<getExternalBulkOrdersHistoryResponse>(
    getGetExternalBulkOrdersHistoryUrl(institutionId, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetExternalBulkOrdersHistoryQueryKey = (
  institutionId?: MaybeRef<string>,
  params?: MaybeRef<GetExternalBulkOrdersHistoryParams>,
) => {
  return [
    "api",
    "frontend",
    "external-bulk-orders",
    "institutions",
    institutionId,
    "history",
    ...(params ? [params] : []),
  ] as const;
};

export const getGetExternalBulkOrdersHistoryQueryOptions = <
  TData = Awaited<ReturnType<typeof getExternalBulkOrdersHistory>>,
  TError = ErrorType<unknown>,
>(
  institutionId: MaybeRef<string>,
  params: MaybeRef<GetExternalBulkOrdersHistoryParams>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getExternalBulkOrdersHistory>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = getGetExternalBulkOrdersHistoryQueryKey(institutionId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getExternalBulkOrdersHistory>>> = ({
    signal,
  }) =>
    getExternalBulkOrdersHistory(unref(institutionId), unref(params), {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: computed(() => !!unref(institutionId)),
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getExternalBulkOrdersHistory>>, TError, TData>;
};

export type GetExternalBulkOrdersHistoryQueryResult = NonNullable<
  Awaited<ReturnType<typeof getExternalBulkOrdersHistory>>
>;
export type GetExternalBulkOrdersHistoryQueryError = ErrorType<unknown>;

export function useGetExternalBulkOrdersHistory<
  TData = Awaited<ReturnType<typeof getExternalBulkOrdersHistory>>,
  TError = ErrorType<unknown>,
>(
  institutionId: MaybeRef<string>,
  params: MaybeRef<GetExternalBulkOrdersHistoryParams>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getExternalBulkOrdersHistory>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryReturnType<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetExternalBulkOrdersHistoryQueryOptions(institutionId, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryReturnType<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = unref(queryOptions).queryKey as DataTag<QueryKey, TData, TError>;

  return query;
}

export type getUserBalanceDueLastMonthResponse200 = {
  data: BalanceReadModel;
  status: 200;
};

export type getUserBalanceDueLastMonthResponseSuccess = getUserBalanceDueLastMonthResponse200 & {
  headers: Headers;
};
export type getUserBalanceDueLastMonthResponse = getUserBalanceDueLastMonthResponseSuccess;

export const getGetUserBalanceDueLastMonthUrl = () => {
  return `/api/frontend/auth/currentUser/balance`;
};

export const getUserBalanceDueLastMonth = async (
  options?: RequestInit,
): Promise<getUserBalanceDueLastMonthResponse> => {
  return customInstance<getUserBalanceDueLastMonthResponse>(getGetUserBalanceDueLastMonthUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetUserBalanceDueLastMonthQueryKey = () => {
  return ["api", "frontend", "auth", "currentUser", "balance"] as const;
};

export const getGetUserBalanceDueLastMonthQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserBalanceDueLastMonth>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getUserBalanceDueLastMonth>>, TError, TData>
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = getGetUserBalanceDueLastMonthQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserBalanceDueLastMonth>>> = ({
    signal,
  }) => getUserBalanceDueLastMonth({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserBalanceDueLastMonth>>,
    TError,
    TData
  >;
};

export type GetUserBalanceDueLastMonthQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserBalanceDueLastMonth>>
>;
export type GetUserBalanceDueLastMonthQueryError = ErrorType<unknown>;

export function useGetUserBalanceDueLastMonth<
  TData = Awaited<ReturnType<typeof getUserBalanceDueLastMonth>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserBalanceDueLastMonth>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryReturnType<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUserBalanceDueLastMonthQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryReturnType<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = unref(queryOptions).queryKey as DataTag<QueryKey, TData, TError>;

  return query;
}

export type changeCurrentUserLocaleResponseDefault = {
  data: void;
  status: number;
};
export type changeCurrentUserLocaleResponseError = changeCurrentUserLocaleResponseDefault & {
  headers: Headers;
};

export type changeCurrentUserLocaleResponse = changeCurrentUserLocaleResponseError;

export const getChangeCurrentUserLocaleUrl = () => {
  return `/api/frontend/auth/currentUser/locale`;
};

export const changeCurrentUserLocale = async (
  saveUserLocaleInputModel: SaveUserLocaleInputModel,
  options?: RequestInit,
): Promise<changeCurrentUserLocaleResponse> => {
  return customInstance<changeCurrentUserLocaleResponse>(getChangeCurrentUserLocaleUrl(), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(saveUserLocaleInputModel),
  });
};

export const getChangeCurrentUserLocaleMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof changeCurrentUserLocale>>,
    TError,
    { data: BodyType<SaveUserLocaleInputModel> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof changeCurrentUserLocale>>,
  TError,
  { data: BodyType<SaveUserLocaleInputModel> },
  TContext
> => {
  const mutationKey = ["changeCurrentUserLocale"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof changeCurrentUserLocale>>,
    { data: BodyType<SaveUserLocaleInputModel> }
  > = (props) => {
    const { data } = props ?? {};

    return changeCurrentUserLocale(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ChangeCurrentUserLocaleMutationResult = NonNullable<
  Awaited<ReturnType<typeof changeCurrentUserLocale>>
>;
export type ChangeCurrentUserLocaleMutationBody = BodyType<SaveUserLocaleInputModel>;
export type ChangeCurrentUserLocaleMutationError = ErrorType<void>;

export const useChangeCurrentUserLocale = <TError = ErrorType<void>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof changeCurrentUserLocale>>,
      TError,
      { data: BodyType<SaveUserLocaleInputModel> },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationReturnType<
  Awaited<ReturnType<typeof changeCurrentUserLocale>>,
  TError,
  { data: BodyType<SaveUserLocaleInputModel> },
  TContext
> => {
  const mutationOptions = getChangeCurrentUserLocaleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
